import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { resetBudgetPerson, saveBudgetPerson, type BudgetAllocation, type BudgetPersonSettings } from '../../api/settingsAiAgent'
import { failed, NoteText, type Note } from './parts'

/**
 * Doua celule pe randul unui om din "Adam access": alocarea lui lunara (suma
 * in USD sau Unlimited, si optional alegerea la 100%) si cat a cheltuit luna
 * asta. Se scrie pe firma omului (`users_info.FirmaID`), prin capetele
 * bugetelor (`/settings/ai_agent/budget/person`) - numai un administrator de
 * platforma, ca tot ecranul.
 *
 * Fara rand omul ia alocarea implicita a firmei (fila Budgets); "Reset" il
 * intoarce acolo. Accesul si adancimea de pe acelasi rand nu se ating de aici.
 */
export function AllocationCells({
  name,
  allocation,
  ready,
  rowSpan,
}: {
  name: string
  allocation: BudgetAllocation | null
  ready: boolean
  /** In "People & access" randul omului se intinde peste firmele lui. */
  rowSpan?: number
}) {
  if (!allocation) {
    return (
      <>
        <td className="text-muted" rowSpan={rowSpan}>
          —
        </td>
        <td className="text-muted" rowSpan={rowSpan}>
          —
        </td>
      </>
    )
  }

  return <Cells key={allocation.custom ? 'c' : 'd'} name={name} allocation={allocation} ready={ready} rowSpan={rowSpan} />
}

function Cells({
  name,
  allocation,
  ready,
  rowSpan,
}: {
  name: string
  allocation: BudgetAllocation
  ready: boolean
  rowSpan?: number
}) {
  const client = useQueryClient()
  const start: BudgetPersonSettings = allocation.custom
    ? allocation.settings
    : { inherit: false, unlimited: allocation.allowance.unlimited, monthly_usd: allocation.allowance.usd, at_limit: '', extra_usd: 0 }
  const [s, setS] = useState<BudgetPersonSettings>(start)
  const [note, setNote] = useState<Note | null>(null)
  const disabled = !ready || allocation.company_id < 1

  const run = (p: Promise<{ ok: boolean; error?: string | null }>, done: string) => {
    setNote({ text: '…' })
    void p
      .then((r) => {
        if (!r.ok) {
          setNote({ text: r.error ?? 'Failed', bad: true })
          return
        }
        setNote({ text: done })
        void client.invalidateQueries({ queryKey: ['settings', 'ai_agent', 'budget'] })
      })
      .catch((e: unknown) => setNote({ text: failed(e), bad: true }))
  }

  const limit = allocation.allowance.unlimited ? null : allocation.allowance.usd

  return (
    <>
      <td rowSpan={rowSpan} style={{ minWidth: 220 }} data-testid={'allocation-' + allocation.user_id}>
        <label style={{ fontWeight: 'normal', margin: 0 }}>
          <input
            type="checkbox"
            aria-label={'Unlimited for ' + name}
            checked={s.unlimited}
            disabled={disabled}
            onChange={(e) => setS({ ...s, unlimited: e.target.checked })}
          />{' '}
          Unlimited
        </label>{' '}
        {!s.unlimited ? (
          <input
            type="number"
            min={0}
            step="0.01"
            aria-label={'Monthly allowance for ' + name}
            className="form-control input-sm"
            style={{ width: 80, display: 'inline-block' }}
            value={String(s.monthly_usd)}
            disabled={disabled}
            onChange={(e) => setS({ ...s, monthly_usd: Number(e.target.value) || 0 })}
          />
        ) : null}
        <select
          className="form-control input-sm"
          aria-label={'At 100% for ' + name}
          style={{ marginTop: 4 }}
          value={s.at_limit}
          disabled={disabled}
          onChange={(e) => setS({ ...s, at_limit: e.target.value as BudgetPersonSettings['at_limit'] })}
        >
          <option value="">At 100%: as the company</option>
          <option value="stop">At 100%: stop</option>
          <option value="cheap_only">At 100%: cheapest model only</option>
          <option value="cheap_extra">At 100%: cheapest + extra</option>
        </select>
        {s.at_limit === 'cheap_extra' ? (
          <input
            type="number"
            min={0}
            step="0.01"
            aria-label={'Extra for ' + name}
            className="form-control input-sm"
            style={{ width: 80, marginTop: 4 }}
            value={String(s.extra_usd)}
            disabled={disabled}
            onChange={(e) => setS({ ...s, extra_usd: Number(e.target.value) || 0 })}
          />
        ) : null}
        <div style={{ marginTop: 4, whiteSpace: 'nowrap' }}>
          <button
            type="button"
            className="btn btn-default btn-xs"
            aria-label={'Save allowance for ' + name}
            disabled={disabled}
            onClick={() => run(saveBudgetPerson(allocation.company_id, allocation.user_id, s), 'Saved')}
          >
            Save allowance
          </button>{' '}
          {allocation.custom ? (
            <button
              type="button"
              className="btn btn-link btn-xs"
              aria-label={'Reset allowance for ' + name}
              disabled={disabled}
              onClick={() => run(resetBudgetPerson(allocation.company_id, allocation.user_id), 'Company default')}
            >
              Reset
            </button>
          ) : (
            <span className="text-muted" style={{ fontSize: 11 }}>
              company default
            </span>
          )}{' '}
          <NoteText note={note} />
        </div>
        {allocation.company ? (
          <div className="text-muted" style={{ fontSize: 11 }}>
            {allocation.company}
          </div>
        ) : allocation.company_id < 1 ? (
          <div className="text-warning" style={{ fontSize: 11 }}>
            No base company on this account, so the allowance cannot be written.
          </div>
        ) : null}
      </td>
      <td rowSpan={rowSpan} style={{ whiteSpace: 'nowrap' }}>
        ${allocation.spent_usd.toFixed(2)}
        {limit !== null ? <span className="text-muted"> of ${limit.toFixed(2)}</span> : <span className="text-muted"> · unlimited</span>}
      </td>
    </>
  )
}
